import React, { useState, useEffect } from 'react';
import { Row, Col, Table, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';

import { useReturnManifestDetailContext } from 'providers/ReturnManifestDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import SearchableInput from 'components/common/SearchableInput';

import { returnManifestApi, shelfApi } from 'api';
import { extractApiErrorMessage } from 'utils/apiErrors';
import { parseLocalDate, formatLocalDate } from 'utils/dateUtils';

const ReturnManifestShipmentExpandedRow = ({ shipment }) => {
  const [lines, setLines] = useState([]);
  const [returnComments, setReturnComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const { startEditing, finishEditing, returnManifestStatus } =
    useReturnManifestDetailContext();

  const { selectedWarehouse } = useUserContext();
  const isExpireDateRequired =
    selectedWarehouse?.is_expire_date_required === true;
  const isReadOnly = ['closed', 'discarded'].includes(returnManifestStatus);

  useEffect(() => {
    if (shipment) {
      const initialLines = (shipment.invoice_status_shipment_lines || []).map(
        line => {
          const isMissing = line.return_inventory_type === 'missing';

          return {
            id: line.id,
            return_inventory_type: line.return_inventory_type || 'a',
            // Saved value first, otherwise default to the original batch.
            // Missing lines never carry shelf/expire values.
            return_shelf: isMissing
              ? null
              : line.return_shelf?.id ?? line.product_batch?.shelf?.id ?? null,
            return_shelf_code: isMissing
              ? ''
              : line.return_shelf?.code ??
                line.product_batch?.shelf?.code ??
                '',
            return_expire_date: isMissing
              ? ''
              : line.return_expire_date ??
                line.product_batch?.expire_date ??
                '',
          };
        },
      );

      setLines(initialLines);
      setReturnComments(shipment.return_comments || '');
      setHasLocalChanges(false);
      setShowValidation(false);
    }
  }, [shipment]);

  const handleLineChange = (lineId, patch) => {
    setLines(prevLines =>
      prevLines.map(line =>
        line.id === lineId ? { ...line, ...patch } : line,
      ),
    );

    setHasLocalChanges(true);
    startEditing(shipment.id);
  };

  const handleInventoryTypeChange = (lineId, newType) => {
    handleLineChange(
      lineId,
      newType === 'missing'
        ? {
            return_inventory_type: newType,
            return_shelf: null,
            return_shelf_code: '',
            return_expire_date: '',
          }
        : { return_inventory_type: newType },
    );
  };

  const handleShelfInputChange = (lineId, term) => {
    handleLineChange(lineId, { return_shelf: null, return_shelf_code: term });
  };

  const handleShelfSelect = (lineId, shelf) => {
    handleLineChange(lineId, {
      return_shelf: shelf.id,
      return_shelf_code: shelf.code,
    });
  };

  const handleExpireDateChange = (lineId, date) => {
    handleLineChange(lineId, {
      return_expire_date: date ? formatLocalDate(date) : '',
    });
  };

  const handleReturnCommentsChange = value => {
    setReturnComments(value);

    setHasLocalChanges(true);
    startEditing(shipment.id);
  };

  const validateLines = () => {
    const errors = [];
    lines.forEach((line, idx) => {
      if (line.return_inventory_type === 'missing') return;
      if (!line.return_shelf) {
        errors.push(`Line ${idx + 1}: Shelf is required.`);
      }
      if (isExpireDateRequired && !line.return_expire_date) {
        errors.push(`Line ${idx + 1}: Expiry date is required.`);
      }
    });
    return errors;
  };

  const handleUpdateSave = async () => {
    if (!shipment || isReadOnly) return;

    const validationErrors = validateLines();
    setShowValidation(validationErrors.length > 0);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    setIsSubmitting(true);

    try {
      // return_shelf_code is UI-only; strip it from the request body
      const processedLines = lines.map(({ return_shelf_code, ...line }) => {
        const isMissing = line.return_inventory_type === 'missing';

        return {
          ...line,
          return_shelf: isMissing ? null : line.return_shelf,
          return_expire_date: isMissing
            ? null
            : line.return_expire_date || null,
        };
      });

      const data = {
        lines: processedLines,
        return_comments: returnComments,
      };

      await returnManifestApi.updateReturnManifestShipment(shipment.id, data);

      toast.success('Successfully updated return manifest shipment');

      setHasLocalChanges(false);
      finishEditing(shipment.id);
    } catch (error) {
      // 429 is already toasted by the global httpClient interceptor
      if (error.response?.status !== 429) {
        toast.error(
          extractApiErrorMessage(error) ||
            'Failed to update return manifest shipment',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shipment) return null;

  return (
    <div
      className={classNames('p-2', {
        'border-bottom': !hasLocalChanges,
        'border border-danger border-2 rounded shadow-sm': hasLocalChanges,
      })}
    >
      <Row>
        <Col md={8}>
          {lines.length > 0 && (
            <div className="border">
              <Table size="sm" bordered className="mb-0">
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>

                <thead className="bg-200">
                  <tr>
                    <th className="text-nowrap text-center fs-11">#</th>
                    <th className="text-nowrap text-start fs-11">SKU</th>
                    <th className="text-nowrap text-start fs-11">
                      Product Name
                    </th>
                    <th className="text-nowrap text-center fs-11">
                      Inventory Category
                    </th>
                    <th className="text-nowrap text-center fs-11">
                      Shelf <span className="text-danger">*</span>
                    </th>
                    <th className="text-nowrap text-center fs-11">
                      Expire Date
                      {isExpireDateRequired && (
                        <span className="text-danger"> *</span>
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {lines.map((line, idx) => {
                    const shipmentLine =
                      shipment.invoice_status_shipment_lines?.find(
                        sl => sl.id === line.id,
                      );
                    const isMissing = line.return_inventory_type === 'missing';

                    return (
                      <tr key={line.id}>
                        <td className="align-middle text-center fs-11">
                          {idx + 1}
                        </td>
                        <td className="align-middle text-start fs-11">
                          {shipmentLine?.product?.sku || '—'}
                        </td>
                        <td className="align-middle text-start fs-11">
                          {shipmentLine?.product?.name || '—'}
                        </td>
                        <td className="text-center align-middle">
                          <Form.Select
                            size="sm"
                            value={line.return_inventory_type}
                            onChange={e =>
                              handleInventoryTypeChange(line.id, e.target.value)
                            }
                            disabled={isReadOnly}
                          >
                            <option value="a">A</option>
                            <option value="b">B</option>
                            <option value="c">C</option>
                            <option value="missing">Missing</option>
                          </Form.Select>
                        </td>
                        <td className="align-middle">
                          <SearchableInput
                            apiService={shelfApi}
                            searchField="code__icontains"
                            additionalParams={
                              selectedWarehouse?.id
                                ? { warehouse: selectedWarehouse.id }
                                : {}
                            }
                            displayField="code"
                            value={line.return_shelf_code}
                            onChange={term =>
                              handleShelfInputChange(line.id, term)
                            }
                            onSelect={shelf =>
                              handleShelfSelect(line.id, shelf)
                            }
                            placeholder={isMissing ? '—' : 'Search shelf...'}
                            renderItem={item => (
                              <div className="fs-11">{item?.code}</div>
                            )}
                            visibleItems={3}
                            disabled={isMissing || isReadOnly}
                            className={
                              showValidation && !isMissing && !line.return_shelf
                                ? 'is-invalid'
                                : ''
                            }
                          />
                        </td>
                        <td className="align-middle">
                          <DatePicker
                            selected={parseLocalDate(line.return_expire_date)}
                            onChange={date =>
                              handleExpireDateChange(line.id, date)
                            }
                            dateFormat="yyyy-MM-dd"
                            className={classNames(
                              'form-control form-control-sm',
                              {
                                'is-invalid':
                                  showValidation &&
                                  !isMissing &&
                                  isExpireDateRequired &&
                                  !line.return_expire_date,
                              },
                            )}
                            placeholderText="YYYY-MM-DD"
                            isClearable={!isMissing && !isReadOnly}
                            disabled={isMissing || isReadOnly}
                            popperProps={{ strategy: 'fixed' }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Col>

        <Col md={4}>
          <div className="d-flex flex-column gap-2 ">
            <Form.Control
              className="fs-10"
              as="textarea"
              rows={1}
              value={returnComments}
              onChange={e => handleReturnCommentsChange(e.target.value)}
              placeholder="Enter return reason..."
              disabled={isReadOnly}
            />

            {hasLocalChanges && (
              <div className="d-flex justify-content-end align-items-center gap-2">
                <span className="text-danger fs-11">*Need to be saved</span>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleUpdateSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ReturnManifestShipmentExpandedRow;
